import { styled, SxProps, Theme } from '@mui/material';
import dynamic from 'next/dynamic';
import { ReactNode } from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  useForm,
  UseFormReturn,
} from 'react-hook-form';

import { isLocal } from 'utils/env.utils';

import { SubmitHandler } from 'components/@forms/types';

const ReactHookFormDevtools = dynamic(() =>
  import(
    /* webpackChunkName: 'ReactHookFormDevtools' */ '@hookform/devtools'
  ).then((c) => c.DevTool),
);

interface Props<FormData extends FieldValues> {
  onSubmit: SubmitHandler<FormData>;
  defaultValues?: DefaultValues<FormData>;
  children: ReactNode | ((methods: UseFormReturn<FormData>) => ReactNode);
  enableDevTools?: boolean;
  sx?: SxProps<Theme>;
}

const StyledForm = styled('form')({});

const Form = <FormData extends FieldValues>({
  defaultValues,
  onSubmit,
  children,
  enableDevTools,
  sx,
}: Props<FormData>) => {
  const methods = useForm<FormData>({ defaultValues });
  return (
    <FormProvider {...methods}>
      <StyledForm
        onSubmit={methods.handleSubmit((data, event) =>
          event ? onSubmit(data, event, methods) : undefined,
        )}
        sx={[...(Array.isArray(sx) ? sx : [sx])]}
      >
        {children instanceof Function ? children(methods) : children}
      </StyledForm>
      {enableDevTools && isLocal && (
        // @ts-expect-error react-hook-form and nextjs dynamic messes up the types
        <ReactHookFormDevtools control={methods.control} />
      )}
    </FormProvider>
  );
};

export default Form;
